/**
 * Team Inbox — EMAIL read pane (spec PART 3.2–3.4 + header-menu redesign).
 *
 * Header-menu redesign (2026-06-22): the stacked action bar + triage strip are
 * GONE — every action is collapsed behind the SAME compact tappable header
 * (<ThreadHeader/>) the WhatsApp Thread uses, so the two channels share one
 * action vocabulary. Reply IS the composer below (no separate Reply button).
 * The read body now fills the height. Subject + sender + "received at care@" chip
 * live on the compact header + a subject heading in the body; the sanitised
 * HTML/linkified body fills the canvas; sent replies appear inline as bubbles.
 *
 * Email items are care@ triage records (draft_contacts), NOT WhatsApp
 * conversations — reached when the open row's channel === 'email'.
 */
import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import type {
  InboxConversation,
  NumberRegistry,
  Teammate,
} from '../lib/types';
import type { LiveClient } from '../../../lib/projectStore';
import { linkifyText } from '../lib/linkify';
import { receivedOnLabel } from '../lib/headerMenu';
import ThreadHeader from '../components/ThreadHeader';
import { EmailComposer } from '../components/EmailComposer';
import { EmptyState } from '../components/furniture';

export type TriageAction = 'create_lead' | 'create_contact' | 'archive' | 'hold';

export interface SentReply {
  id: string;
  at: number;
  body: string;
}


/** Render a text paragraph with its lines linkified + line breaks preserved. */
function TextParagraph({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <p>
      {lines.map((ln, i) => (
        <span key={i}>
          {linkifyText(ln)}
          {i < lines.length - 1 ? <br /> : null}
        </span>
      ))}
    </p>
  );
}

function timeLabel(ms: number | null): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return '';
  }
}

export default function EmailThread({
  conversation,
  myId,
  isAdmin = false,
  canAssign,
  teammates,
  registry,
  resolved = null,
  linkProject = true,
  sentReplies,
  replying,
  replyErr,
  triageBusy,
  onBack,
  onReply,
  onTriage,
  onCreateLead,
  onCreateContact,
  onAddToExisting,
  onAssign,
  isHeld = false,
  onUnhold,
}: {
  conversation: InboxConversation | null;
  myId: string | null;
  isAdmin?: boolean;
  canAssign: boolean;
  teammates: Teammate[];
  /** Number registry — resolves the received-on chip (care@ for email). */
  registry: NumberRegistry;
  /** Resolved known contact when the sender matched a client (drives the header). */
  resolved?: LiveClient | null;
  /** false in the dev harness (no router) — disables the project link. */
  linkProject?: boolean;
  sentReplies: SentReply[];
  replying: boolean;
  replyErr: string | null;
  triageBusy: boolean;
  onBack: () => void;
  onReply: (body: string) => Promise<boolean>;
  /** Archive / Hold (Create Lead/Contact go through the form openers below). */
  onTriage: (action: TriageAction) => void;
  /** Open the Create-Lead form (host shows the modal). */
  onCreateLead?: () => void;
  /** Open the Create-Contact form (host shows the modal). */
  onCreateContact?: () => void;
  /** "Add to existing" — open the two-tab picker to link this email's sender. */
  onAddToExisting?: () => void;
  onAssign: (staffId: string, name: string) => Promise<boolean>;
  /** This email is currently Held (parked) — show the un-hold recovery banner. */
  isHeld?: boolean;
  /** Un-hold (return to the active queue). */
  onUnhold?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const composerRef = useRef<HTMLDivElement | null>(null);

  const em = conversation?.email || null;
  // Close the menu when the open email changes.
  const draftKey = conversation?.key || null;
  useEffect(() => {
    setMenuOpen(false);
  }, [draftKey]);

  if (!conversation || !em) {
    return (
      <div className="ti-thread">
        <div className="ti-thread-empty">
          <EmptyState icon="✉">
            Pick an email on the left to read and reply.
          </EmptyState>
        </div>
      </div>
    );
  }

  const actioned = !!em.triageAction;

  // Sanitised HTML body. Force every link to open safely in a new tab (a
  // confirmation/activation link from an inbound is untrusted) — add a hook
  // ONCE per render that stamps target=_blank + rel=noopener on anchors and
  // strips javascript: hrefs (DOMPurify already drops them, this is defence).
  const bodyHtml = em.bodyHtml
    ? (() => {
        DOMPurify.addHook('afterSanitizeAttributes', (node) => {
          if (node.tagName === 'A') {
            node.setAttribute('target', '_blank');
            node.setAttribute('rel', 'noopener noreferrer');
          }
        });
        const clean = DOMPurify.sanitize(em.bodyHtml as string, {
          USE_PROFILES: { html: true },
          ADD_ATTR: ['target'],
        });
        DOMPurify.removeHook('afterSanitizeAttributes');
        return clean;
      })()
    : null;
  // Plain-text body → paragraphs on blank lines; single newlines kept as <br>.
  const paragraphs = em.bodyText
    ? em.bodyText.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
    : [];

  const received = receivedOnLabel(conversation, registry);

  return (
    <div className="ti-thread ti-email">
      {/* compact clickable header + the actions slide-over / bottom sheet.
          Reply IS the composer below (no separate Reply button). */}
      <ThreadHeader
        conversation={conversation}
        received={received}
        resolved={resolved}
        myId={myId}
        isAdmin={isAdmin}
        canAssign={canAssign}
        teammates={teammates}
        linkProject={linkProject}
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        onBack={onBack}
        busy={triageBusy}
        isHeld={isHeld}
        actions={{
          onCreateLead: !actioned ? onCreateLead : undefined,
          onCreateContact: !actioned ? onCreateContact : undefined,
          onAddToExisting: !actioned ? onAddToExisting : undefined,
          onArchive: !actioned ? () => onTriage('archive') : undefined,
          onHold: !actioned && !isHeld ? () => onTriage('hold') : undefined,
          onUnhold: isHeld ? onUnhold : undefined,
          onAssign: (staffId, name) => void onAssign(staffId, name),
        }}
      />

      <div className="ti-eread-body-scroll">
        {/* subject heading — the compact header truncates; the body shows it full */}
        <div className="ti-eread-subjhead">
          <h2 className="ti-eread-subj">{em.subject}</h2>
          <span className="ti-eread-recv">
            <span className="ti-recv-chip">↘ received at {em.receivedAtAddress}</span>
            {em.receivedAtMs && (
              <span className="ti-recv-tm">{timeLabel(em.receivedAtMs)} IST</span>
            )}
          </span>
        </div>

        {/* email body */}
        <div className="ti-eread-body">
          {bodyHtml ? (
            <div
              className="ti-eread-html"
              // Sanitised above via DOMPurify (html profile).
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : paragraphs.length > 0 ? (
            paragraphs.map((p, i) => <TextParagraph key={i} text={p} />)
          ) : (
            <p className="ti-eread-empty">(No message body.)</p>
          )}
        </div>

        {/* sent replies — inline outbound bubbles */}
        {sentReplies.map((r) => (
          <div key={r.id} className="ti-esent">
            <div className="ti-esent-head">
              You · sent {timeLabel(r.at)} IST · from {em.receivedAtAddress}
            </div>
            <div className="ti-esent-body">
              {r.body.split(/\n{2,}/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        ))}
      </div>

      {replyErr && (
        <div className="ti-send-err" role="alert">
          {replyErr}
        </div>
      )}

      <div ref={composerRef}>
        <EmailComposer
          toName={em.senderName || em.senderEmail}
          fromAddress={em.receivedAtAddress}
          sending={replying}
          onSend={onReply}
        />
      </div>
    </div>
  );
}
